/**
 * Structured Logging with Sentry Integration
 * 
 * Replaces console.error with structured logging that sends to Sentry.
 * Use logger.error() instead of console.error() for production error tracking.
 * 
 * Usage:
 *   logger.trace('Starting process', { step: 1 });
 *   logger.debug(logger.fmt`Cache miss for user: ${userId}`);
 *   logger.info('Profile updated', { profileId: 123 });
 *   logger.warn('Rate limit approaching', { remaining: 10 });
 *   logger.error('Failed to save', { error: err.message });
 *   logger.fatal('Database connection lost', { retries: 3 });
 */

import * as Sentry from '@sentry/react';
import { isSentryInitialized } from './sentry';

/**
 * Template literal function for structured logging
 * Extracts variables from template strings
 */
function fmt(strings, ...values) {
  return strings.reduce((acc, str, i) => {
    return acc + str + (values[i] !== undefined ? values[i] : '');
  }, '');
}

/**
 * Log levels (aligned with Sentry)
 */
const LogLevel = {
  TRACE: 'trace',
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
  FATAL: 'fatal',
};

/**
 * Internal log function
 */
function log(level, message, context = {}) {
  // Always log to console in development
  if (import.meta.env.DEV) {
    const consoleMethod = level === 'error' || level === 'fatal' ? 'error' : 
                         level === 'warn' ? 'warn' : 'log';
    console[consoleMethod](`[${level.toUpperCase()}]`, message, context);
  }

  // Send to Sentry if initialized
  if (isSentryInitialized()) {
    // For errors and fatal, capture as exception
    if (level === 'error' || level === 'fatal') {
      Sentry.captureException(new Error(message), {
        level: level === 'fatal' ? 'fatal' : 'error',
        contexts: {
          log: context,
        },
      });
    } else {
      // For other levels, add as breadcrumb
      Sentry.addBreadcrumb({
        level,
        message,
        data: context,
        category: 'log',
      });
    }
  }
}

/**
 * Logger API
 */
export const logger = {
  /**
   * Template literal function for dynamic values
   * Usage: logger.fmt`User ${userId} logged in`
   */
  fmt,

  /**
   * Trace level - very detailed debugging
   */
  trace(message, context) {
    log(LogLevel.TRACE, message, context);
  },

  /**
   * Debug level - debugging information
   */
  debug(message, context) {
    log(LogLevel.DEBUG, message, context);
  },

  /**
   * Info level - informational messages
   */
  info(message, context) {
    log(LogLevel.INFO, message, context);
  },

  /**
   * Warn level - warning messages
   */
  warn(message, context) {
    log(LogLevel.WARN, message, context);
  },

  /**
   * Error level - error messages (sent to Sentry)
   */
  error(message, context) {
    log(LogLevel.ERROR, message, context);
  },

  /**
   * Fatal level - critical errors (sent to Sentry)
   */
  fatal(message, context) {
    log(LogLevel.FATAL, message, context);
  },
};

/**
 * Export for convenience
 */
export default logger;

